// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Plus, Trash2, Lock, Share2, TrendingUp, Info, BarChart3, AlertTriangle } from 'lucide-react';
import { HistoricalAnalysisBox, VARRiskBox } from '../components/portfolio/HistoricalAndVAR';
import { Doughnut, Line } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title, Filler } from 'chart.js';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title, Filler);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => {
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '0';
};
const fmtPct = (n) => {
  const v = Number(n);
  return (Number.isFinite(v) ? v : 0).toFixed(2) + '%';
};
const DISCLAIMER = 'Simulatore educativo — i portafogli creati sono ipotetici e non rappresentano il tuo portafoglio reale. Non costituisce consulenza finanziaria ai sensi della Direttiva MiFID II.';

const SCENARIOS = [
  { id: 'crisi08', label: 'Crisi 2008',     equity: -0.40, bond: +0.05, crypto: -0.50, gold: +0.20, real_estate: -0.40 },
  { id: 'covid20', label: 'COVID 2020',     equity: -0.30, bond: +0.03, crypto: -0.30, gold: +0.10, real_estate: -0.15 },
  { id: 'tassi22', label: 'Rialzo tassi 2022', equity: -0.18, bond: -0.15, crypto: -0.65, gold: +0.12, real_estate: -0.10 },
  { id: 'bull',    label: 'Bull 2013–2022', equity: +2.85, bond: +0.30, crypto: +9.00, gold: +0.25, real_estate: +1.20 },
];

const TYPE_SCENARIO_MAP = {
  'ETF': 'equity', 'ETC': 'gold', 'Azione': 'equity',
  'Fondo Comune': 'equity', 'Titolo di Stato': 'bond',
  'Conto Deposito': 'bond', 'Materia Prima': 'gold',
  'Crypto': 'crypto',
};

const TAX_LABELS = {
  'efficient':   { label: 'Fiscalmente efficiente', icon: '✅', color: '#3D7A52', note: 'Accumulazione — nessuna tassazione annuale' },
  'distribution':{ label: 'Dividendi tassati 26%',  icon: '⚠️', color: '#E8903A', note: 'Ogni dividendo è soggetto a ritenuta 26%' },
  'agevolata':   { label: 'Aliquota agevolata 12.5%',icon: '✅', color: '#3D7A52', note: 'BTP/BOT godono di tassazione ridotta al 12.5%' },
  'inefficient': { label: 'Meno efficiente fiscalmente', icon: '⚠️', color: '#E8903A', note: 'Interessi o plusvalenze tassate al 26%' },
  'standard':    { label: 'Plusvalenze tassate 26%', icon: '⚠️', color: '#E8903A', note: 'Capital gain soggetto a tassazione standard' },
};

const CHART_COLORS = ['#C9A84C','#3D7A52','#4A6FA5','#C0392B','#8E44AD','#E8903A','#1ABC9C','#E74C3C','#2ECC71','#F39C12'];

export default function SimulatorePortafoglio() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPremium = user?.tier === 'premium';

  const [instruments, setInstruments] = useState([]);
  const [portfolio, setPortfolio] = useState([]); // [{inst, amount}]
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Tutti');
  const [activeTab, setActiveTab] = useState('portfolio');
  const [showPremiumPrompt, setShowPremiumPrompt] = useState(null);

  // Deployment 14 — P3: projection chart state
  const [horizon, setHorizon] = useState(10);
  const [showRealReturn, setShowRealReturn] = useState(false);
  const [inflation, setInflation] = useState('2.0');

  const [instrumentsCount, setInstrumentsCount] = useState(null);

  // Deployment 16 — P1: accordion ℹ️ per instrument (one at a time)
  const [expandedRow, setExpandedRow] = useState(null);

  // Deployment 16 — P1: historical returns (CAGR per asset class)
  const [historicalReturns, setHistoricalReturns] = useState(null);

  // Deployment 16 — P1: historical chart controls
  const currentYear = 2024;
  const [histStartYear, setHistStartYear] = useState(2014);
  const [histAmount, setHistAmount] = useState(10000);
  const [histOpen, setHistOpen] = useState(false);
  const [varOpen, setVarOpen] = useState(true);

  // Deployment 23 — Sezione D: auto-apertura del box "Analisi storica"
  // quando il portafoglio contiene almeno uno strumento. Non si apre
  // al caricamento iniziale (default false).
  useEffect(() => {
    if (portfolio.length > 0) {
      setHistOpen(true);
    }
  }, [portfolio.length]);

  useEffect(() => {
    fetch(`${API_URL}/api/config/historical-returns`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setHistoricalReturns(d); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/portfolio/instruments/count`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && Number.isFinite(d.count)) setInstrumentsCount(d.count); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/portfolio/instruments`)
      .then(r => r.json())
      .then(d => {
        // The endpoint may return either a plain array OR { instruments: [...] }.
        // Normalize both shapes and harmonise the field names with what the UI
        // expects (id, name, ticker, category, tier, type, ...).
        const raw = Array.isArray(d) ? d : (Array.isArray(d?.instruments) ? d.instruments : []);
        const normalised = raw.map(item => ({
          ...item,
          id:               item.id ?? item.instrument_id ?? item._id ?? item.ticker ?? item.label,
          name:             item.name ?? item.label ?? item.title ?? '',
          ticker:           item.ticker ?? item.symbol ?? item.code ?? (item.label || '').split(' ').slice(0, 2).join(' '),
          category:         item.category ?? item.group ?? item.asset_class ?? 'Altri',
          tier:             item.tier ?? 1,
          type:             item.type ?? item.instrument_type ?? item.group ?? item.category ?? 'ETF',
          geo:              (item.geo && typeof item.geo === 'object') ? item.geo : (item.region && typeof item.region === 'object' ? item.region : {}),
          ter:              Number.isFinite(Number(item.ter)) ? Number(item.ter) : 0,
          tax_efficiency:   item.tax_efficiency ?? 'standard',
        })).filter(it => it.id && it.name);
        setInstruments(normalised);
      })
      .catch(() => setInstruments([]));
  }, []);

  const categories = useMemo(() => {
    const cats = [...new Set(instruments.map(i => i.category))].sort();
    return ['Tutti', ...cats];
  }, [instruments]);

  const filteredInstruments = useMemo(() => {
    let list = instruments;
    if (category !== 'Tutti') list = list.filter(i => i.category === category);
    if (search) list = list.filter(i =>
      i.name.toLowerCase().includes(search.toLowerCase()) ||
      i.ticker.toLowerCase().includes(search.toLowerCase())
    );
    return list;
  }, [instruments, category, search]);

  const allTier2 = useMemo(() => instruments.filter(i => i.tier === 2), [instruments]);

  // Deployment 19 — Sticker "Aggiornato il GG/MM/AAAA" da data_aggiornamento_justetf
  const formatJustETFDate = (d) => {
    if (!d) return null;
    try {
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return null;
      const gg = String(dt.getDate()).padStart(2, '0');
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      const yyyy = dt.getFullYear();
      return `${gg}/${mm}/${yyyy}`;
    } catch { return null; }
  };

  const addInstrument = useCallback((inst) => {
    if (inst.tier === 2 && !isPremium) {
      setShowPremiumPrompt(inst);
      return;
    }
    setPortfolio(prev => {
      const existing = prev.findIndex(p => p.inst.id === inst.id);
      if (existing >= 0) return prev;
      return [...prev, { inst, amount: 10000 }];
    });
  }, [isPremium]);

  const removeInstrument = useCallback((id) => {
    setPortfolio(prev => prev.filter(p => p.inst.id !== id));
  }, []);

  const updateAmount = useCallback((id, val) => {
    setPortfolio(prev => prev.map(p => p.inst.id === id ? { ...p, amount: Number(val) || 0 } : p));
  }, []);

  // ─── Analytics ───────────────────────────────────────────────────────────
  // Deployment 16 — Volatilità fallback per tipo (quando strumento.volatility === null)
  const VOL_FALLBACK = {
    'ETF Azionari': 0.16, 'Azionario': 0.16, 'Azioni': 0.28, 'Stock': 0.28,
    'ETF Obbligazionari': 0.05, 'Obbligazionari': 0.05, 'Bond': 0.05,
    'Crypto': 0.70,
    'Liquidità': 0.005, 'Conto': 0.005,
    'Materie Prime': 0.15, 'Commodity': 0.15,
    'ETF Immobiliari': 0.19, 'REIT': 0.19,
  };
  const resolveVolatility = (inst) => {
    if (typeof inst.volatility === 'number') return { v: inst.volatility, isFallback: false };
    const tags = [inst.type, inst.category, inst.historical_benchmark].filter(Boolean).join(' ');
    for (const [k, v] of Object.entries(VOL_FALLBACK)) {
      if (tags.toLowerCase().includes(k.toLowerCase())) return { v, isFallback: true };
    }
    return { v: 0.15, isFallback: true };
  };

  const calculateWeightedVolatility = (items, total) => {
    if (!items.length || total <= 0) return { vol: 0, partial: false };
    let vol = 0, partial = false;
    items.forEach(p => {
      const { v, isFallback } = resolveVolatility(p.inst);
      vol += (p.amount / total) * v;
      if (isFallback) partial = true;
    });
    return { vol, partial };
  };

  const analytics = useMemo(() => {
    if (!portfolio.length) return null;
    const total = portfolio.reduce((s, p) => s + p.amount, 0);
    if (total === 0) return null;

    // Weighted TER
    const wTer = portfolio.reduce((s, p) => s + (p.inst.ter * p.amount), 0) / total;
    const annualCost = total * wTer / 100;
    const lowCostAnnual = total * 0.15 / 100;
    const saving20y = (annualCost - lowCostAnnual) * 20;

    // Type distribution
    const byType = {};
    portfolio.forEach(p => {
      const t = p.inst.type || 'Altro';
      byType[t] = (byType[t] || 0) + p.amount;
    });

    // ── Deployment 16 — Volatilità stimata (weighted) ───────────────────────
    const { vol: wVol } = calculateWeightedVolatility(portfolio, total);
    // Deployment 18 — B3: nota "Stima parziale" solo se almeno uno strumento ha volatility == null
    const volPartial = portfolio.some(p => p.inst.volatility == null);

    // ── Deployment 16 — Rendimento atteso annuo (stessa fonte del grafico "Base") ─
    let wRet = 0, retPartial = false;
    let retWeightSum = 0;
    portfolio.forEach(p => {
      const r = p.inst.expected_return ?? p.inst.ret;
      if (typeof r === 'number' && !Number.isNaN(r)) {
        wRet += (p.amount / total) * r;
        retWeightSum += p.amount;
      } else {
        retPartial = true;
      }
    });
    // Ret stored as integer percent in DB (e.g., 7.5) — normalise if decimal.
    // Keep the percentage number as-is; used as "X%/anno".
    const expectedReturnPct = wRet;
    const growth10y = total * (Math.pow(1 + expectedReturnPct / 100, 10) - 1);

    return {
      total, wTer, annualCost, lowCostAnnual, saving20y, byType,
      // Deployment 16 — new KPIs
      wVol, volPartial,
      expectedReturnPct, retPartial, growth10y,
    };
  }, [portfolio]);

  const terColor = analytics ? (analytics.wTer < 0.3 ? '#3D7A52' : analytics.wTer < 1.0 ? '#E8903A' : '#C0392B') : '#888780';

  // Donut chart data
  const donutData = useMemo(() => {
    if (!analytics) return null;
    const labels = Object.keys(analytics.byType);
    const data = labels.map(k => Math.round((analytics.byType[k] / analytics.total) * 100));
    return {
      labels,
      datasets: [{ data, backgroundColor: CHART_COLORS.slice(0, labels.length), borderWidth: 0 }]
    };
  }, [analytics]);

  const donutOptions = { plugins: { legend: { position: 'bottom', labels: { color: '#888780', font: { size: 11 } } } }, cutout: '65%' };

  // ─── Scenarios ───────────────────────────────────────────────────────────
  const scenarioResults = useMemo(() => {
    if (!analytics || !portfolio.length) return [];
    return SCENARIOS.map(sc => {
      const finalVal = portfolio.reduce((sum, p) => {
        const assetClass = TYPE_SCENARIO_MAP[p.inst.type] || 'equity';
        const ret = sc[assetClass] ?? sc.equity;
        return sum + p.amount * (1 + ret);
      }, 0);
      const diff = finalVal - analytics.total;
      const pct = (diff / analytics.total) * 100;
      return { ...sc, finalVal, diff, pct };
    });
  }, [analytics, portfolio]);

  // ─── P3 Projection chart (Base / Ottimista / Pessimista) ─────────────────
  // Deployment 16 — Base line uses analytics.expectedReturnPct so the KPI card
  // "Rendimento atteso annuo" is guaranteed to match the chart legend.
  const projection = useMemo(() => {
    if (!analytics || !portfolio.length || analytics.total <= 0) return null;
    const baseRet = analytics.expectedReturnPct;
    const inflPct = showRealReturn ? (Number(inflation) || 0) : 0;
    const baseAdj = baseRet - inflPct;
    const optAdj  = baseRet + 3 - inflPct;
    const pesAdj  = baseRet - 3 - inflPct;

    const labels = ['0'];
    const baseSerie = [analytics.total];
    const optSerie  = [analytics.total];
    const pesSerie  = [analytics.total];
    for (let y = 1; y <= horizon; y++) {
      labels.push(String(y));
      baseSerie.push(analytics.total * Math.pow(1 + baseAdj / 100, y));
      optSerie.push(analytics.total * Math.pow(1 + optAdj / 100, y));
      pesSerie.push(analytics.total * Math.pow(1 + pesAdj / 100, y));
    }
    return {
      baseRet, baseAdj, optAdj, pesAdj, labels,
      data: {
        labels,
        datasets: [
          { label: `Base (${baseAdj.toFixed(1)}%)`,        data: baseSerie, borderColor: '#C9A84C', backgroundColor: 'rgba(201,168,76,0.15)', tension: 0.3, fill: false, borderWidth: 2 },
          { label: `Ottimista (${optAdj.toFixed(1)}%)`,    data: optSerie,  borderColor: '#3D7A52', backgroundColor: 'rgba(61,122,82,0.10)',  tension: 0.3, fill: false, borderWidth: 2, borderDash: [4,4] },
          { label: `Pessimista (${pesAdj.toFixed(1)}%)`,   data: pesSerie,  borderColor: '#E8903A', backgroundColor: 'rgba(232,144,58,0.10)', tension: 0.3, fill: false, borderWidth: 2, borderDash: [4,4] },
        ],
      },
    };
  }, [analytics, portfolio, horizon, showRealReturn, inflation]);

  const projOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { color: '#888780', font: { size: 11 }, padding: 14 } },
      tooltip: { callbacks: { label: (c) => `${c.dataset.label}: € ${fmt(c.parsed.y)}` } },
    },
    scales: {
      x: { ticks: { color: '#888780' }, grid: { color: 'rgba(255,255,255,0.04)' }, title: { display: true, text: 'Anni', color: '#888780' } },
      y: { ticks: { color: '#888780', callback: (v) => '€ ' + fmt(v) }, grid: { color: 'rgba(255,255,255,0.04)' } },
    },
  };

  return (
    <div className="min-h-screen bg-[#141412]" data-testid="portfolio-simulator">
      <Helmet><title>Simulatore Portafoglio — SAPIENS</title></Helmet>

      {/* Header */}
      <div style={{ background: '#1C1C1A', borderBottom: '1px solid rgba(255,255,255,0.06)' }} className="py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl font-medium text-[#E8E8E4] mb-2">Simulatore Portafoglio Educativo</h1>
          <p className="text-[#888780]">Costruisci portafogli ipotetici, analizza costi e diversificazione. Solo a scopo educativo.</p>
          <div className="mt-3 text-xs text-[#888780] p-3 rounded-lg" style={{ background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.15)' }}>
            {DISCLAIMER}
          </div>
          {!isPremium && (
            <div className="mt-3 text-sm text-[#888780] p-3 rounded-lg flex items-center justify-between" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <span>Stai usando il <strong style={{ color: '#E8E8E4' }}>piano gratuito</strong> — solo strumenti Tier 1. <span style={{ color: '#C9A84C' }}>Premium: accesso a tutti gli strumenti Premium, scenari e analisi fiscale.</span></span>
              <button onClick={() => navigate('/premium')} className="ml-4 text-xs px-3 py-1.5 rounded-lg font-medium flex-shrink-0" style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: '#C9A84C' }}>
                Scopri Premium →
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">

        {/* ── INSTRUMENT PICKER ── */}
        <div>
          <h2 className="text-lg font-medium text-[#E8E8E4] mb-4">Aggiungi strumenti al portafoglio</h2>
          <div className="flex gap-3 mb-4 flex-wrap">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca ETF, azione, crypto..."
              className="flex-1 min-w-48 bg-[#252523] border border-white/10 rounded-lg px-4 py-2 text-sm text-[#E8E8E4] placeholder-[#888780] focus:border-[#C9A84C]/40 outline-none"
              data-testid="portfolio-search" />
            <select value={category} onChange={e => setCategory(e.target.value)}
              className="bg-[#252523] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#E8E8E4] focus:border-[#C9A84C]/40 outline-none"
              data-testid="portfolio-category-filter">
              {categories.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
            {filteredInstruments.map(inst => {
              const inPortfolio = portfolio.some(p => p.inst.id === inst.id);
              const isLocked = inst.tier === 2 && !isPremium;
              const justETFDate = formatJustETFDate(inst.data_aggiornamento_justetf);
              return (
                <button key={inst.id} onClick={() => addInstrument(inst)} disabled={inPortfolio}
                  className="flex items-center gap-3 p-3 rounded-lg text-left transition-all hover:border-[#C9A84C]/30 disabled:opacity-40"
                  style={{ background: inPortfolio ? 'rgba(201,168,76,0.06)' : '#252523', border: `1px solid ${inPortfolio ? 'rgba(201,168,76,0.3)' : isLocked ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.06)'}`, opacity: isLocked ? 0.75 : 1 }}
                  data-testid={`add-instrument-${inst.id}`}>
                  {isLocked
                    ? <Lock className="w-4 h-4 flex-shrink-0 text-[#888780]" />
                    : <Plus className="w-4 h-4 flex-shrink-0" style={{ color: inPortfolio ? '#C9A84C' : '#888780' }} />}
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-[#E8E8E4] truncate">{inst.ticker}</div>
                    <div className="text-[10px] text-[#888780] truncate">{inst.name}</div>
                    {justETFDate && (
                      <div
                        className="text-[9px] truncate mt-0.5"
                        style={{ color: '#d4a843', letterSpacing: '0.02em' }}
                        data-testid={`justetf-sticker-${inst.id}`}
                      >
                        Aggiornato il {justETFDate}
                      </div>
                    )}
                  </div>
                  <div className="ml-auto flex-shrink-0 flex gap-1">
                    {isLocked && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(201,168,76,0.1)', color: '#C9A84C' }}>PRO</span>}
                    <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.06)', color: '#888780' }}>{inst.type}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Tier 2 note for free users - instruments shown with lock icon above */}
        </div>

        {/* ── PORTFOLIO TABLE ── */}
        {portfolio.length > 0 && (
          <div data-testid="portfolio-table">
            <h2 className="text-lg font-medium text-[#E8E8E4] mb-4">Il tuo portafoglio ipotetico</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    <th className="text-left py-2 px-3 text-[#888780] font-normal">Strumento</th>
                    <th className="text-left py-2 px-3 text-[#888780] font-normal">Tipo</th>
                    <th className="text-right py-2 px-3 text-[#888780] font-normal">Importo (€)</th>
                    <th className="text-right py-2 px-3 text-[#888780] font-normal">TER %</th>
                    <th className="text-right py-2 px-3 text-[#888780] font-normal">Costo/anno</th>
                    <th className="py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {portfolio.map((p, i) => {
                    const isOpen = expandedRow === p.inst.id;
                    const inst = p.inst;
                    const { v: volValue } = resolveVolatility(inst);
                    const tLower = (inst.type || '').toLowerCase();
                    const cLower = (inst.category || '').toLowerCase();
                    const hbLower = (inst.historical_benchmark || '').toLowerCase();
                    const isCrypto = tLower.includes('crypto') || hbLower === 'crypto';
                    const isStock = !isCrypto && (tLower.includes('stock') || tLower.includes('azione') || cLower === 'azioni' || hbLower === 'azioni singole');
                    const isEtf = !isCrypto && !isStock && (tLower.includes('etf') || cLower.startsWith('etf'));
                    return (
                    <React.Fragment key={inst.id}>
                    <tr style={{ borderBottom: isOpen ? 'none' : '1px solid rgba(255,255,255,0.05)', background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                      <td className="py-2 px-3">
                        <div className="font-medium text-[#E8E8E4]">{inst.ticker}</div>
                        <div className="text-xs text-[#888780] truncate max-w-xs">{inst.name}</div>
                      </td>
                      <td className="py-2 px-3 text-[#888780] text-xs">{inst.type}</td>
                      <td className="py-2 px-3 text-right">
                        <input type="number" value={p.amount} onChange={e => updateAmount(inst.id, e.target.value)}
                          className="w-28 bg-[#252523] border border-white/10 rounded px-2 py-1 text-[#E8E8E4] text-sm text-right focus:border-[#C9A84C]/40 outline-none"
                          data-testid={`amount-${inst.id}`} />
                      </td>
                      <td className="py-2 px-3 text-right text-[#888780]">{fmtPct(inst.ter)}</td>
                      <td className="py-2 px-3 text-right text-[#888780]">€ {fmt(p.amount * inst.ter / 100)}</td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2 justify-end flex-col sm:flex-row">
                          <button
                            onClick={() => setExpandedRow(isOpen ? null : inst.id)}
                            className="text-[#888780] hover:text-[#C9A84C] transition-colors"
                            title="Dettagli strumento"
                            data-testid={`info-${inst.id}`}
                            aria-label="Dettagli strumento"
                          >
                            <Info className="w-4 h-4" />
                          </button>
                          <button onClick={() => removeInstrument(inst.id)} className="text-[#888780] hover:text-[#C0392B] transition-colors" data-testid={`remove-${inst.id}`} aria-label="Rimuovi strumento">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(201,168,76,0.04)' }} data-testid={`accordion-${inst.id}`}>
                        <td colSpan={6} className="px-4 py-3">
                          <div className="text-xs text-[#C5C5BE] space-y-1.5">
                            <div>
                              <span className="text-[#888780]">Volatilità annua stimata:</span>{' '}
                              <strong className="text-[#E8E8E4]">{(volValue * 100).toFixed(1)}%</strong>
                            </div>
                            <div>
                              <span className="text-[#888780]">Duration:</span>{' '}
                              <strong className="text-[#E8E8E4]">
                                {typeof inst.duration_years === 'number' ? `${inst.duration_years} anni` : 'N/A'}
                              </strong>
                            </div>
                            {/* Deployment 17 — P0 Fix 4: ETF kid_url with Borsa Italiana fallback */}
                            {isEtf && inst.kid_url && (
                              <div>
                                <a href={inst.kid_url} target="_blank" rel="noreferrer" className="text-[#C9A84C] hover:underline">
                                  📄 Documento informativo ufficiale (KID) →
                                </a>
                              </div>
                            )}
                            {isEtf && !inst.kid_url && (
                              <div data-testid={`accordion-kid-fallback-${inst.id}`}>
                                {inst.isin ? (
                                  <a href={`https://www.borsaitaliana.it/borsa/etf/scheda/${inst.isin}-ETFP.html`} target="_blank" rel="noreferrer" className="text-[#C9A84C] hover:underline">
                                    📄 Cerca il KID su Borsa Italiana →
                                  </a>
                                ) : (
                                  <a href="https://www.borsaitaliana.it" target="_blank" rel="noreferrer" className="text-[#C9A84C] hover:underline">
                                    📄 Cerca il KID su Borsa Italiana → borsaitaliana.it
                                  </a>
                                )}
                              </div>
                            )}
                            {/* Deployment 17 — P0 Fix 5: info_url for single stocks (Yahoo / Borsa Italiana) */}
                            {isStock && inst.info_url && (
                              <div>
                                <a href={inst.info_url} target="_blank" rel="noreferrer" className="text-[#C9A84C] hover:underline" data-testid={`accordion-info-link-${inst.id}`}>
                                  🔗 Scheda su {inst.info_url.includes('borsaitaliana') ? 'Borsa Italiana' : inst.info_url.includes('yahoo') ? 'Yahoo Finance' : 'sito ufficiale'} →
                                </a>
                              </div>
                            )}
                            {isCrypto && (
                              <div className="text-[10px] text-[#888780] italic">Nessun documento informativo disponibile per le crypto.</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );})}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── ANALYSIS PANEL ── */}
        {analytics && (
          <div data-testid="portfolio-analysis">
            {/* Tab nav — Deployment 17 P0 Fix 3: Scenari ora libera. Solo "tax" rimane Premium */}
            <div className="flex gap-1 mb-6 p-1 rounded-lg w-fit" style={{ background: 'rgba(255,255,255,0.04)' }}>
              {[['portfolio', 'Analisi'], ['scenarios', 'Scenari'], ['tax', 'Fiscalità']].map(([tab, label]) => {
                const isLocked = tab === 'tax' && !isPremium;
                return (
                  <button key={tab} onClick={() => isLocked ? navigate('/premium') : setActiveTab(tab)}
                    className="px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-1"
                    style={{ background: activeTab === tab ? '#C9A84C' : 'transparent', color: activeTab === tab ? '#1C1C1A' : '#888780' }}
                    data-testid={`tab-${tab}`}>
                    {isLocked && <Lock className="w-3 h-3" />}
                    {label}
                  </button>
                );
              })}
            </div>

            {activeTab === 'portfolio' && (
              <div className="space-y-4" data-testid="tab-portfolio-content">
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Total */}
                  <div style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 12, padding: '16px' }} data-testid="portfolio-total">
                    <div className="text-xs text-[#888780] mb-1">Valore ipotetico totale</div>
                    <div className="text-2xl font-bold text-[#C9A84C]">€ {fmt(analytics.total)}</div>
                  </div>
                  {/* TER */}
                  <div style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '16px' }} data-testid="portfolio-ter">
                    <div className="text-xs text-[#888780] mb-1">TER medio ponderato</div>
                    <div className="text-2xl font-bold" style={{ color: terColor }}>{fmtPct(analytics.wTer)}/anno</div>
                    <div className="text-xs mt-1" style={{ color: terColor }}>
                      Costo annuo stimato: €{fmt(analytics.annualCost)}
                    </div>
                  </div>
                  {/* Deployment 16 — Volatilità stimata (sostituisce Esposizione geografica) */}
                  <div style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '16px' }} data-testid="portfolio-volatility">
                    <div className="text-xs text-[#888780] mb-1">Volatilità stimata</div>
                    <div className="text-2xl font-bold text-[#E8E8E4]" data-testid="portfolio-volatility-value">
                      {(analytics.wVol * 100).toFixed(1)}%/anno
                    </div>
                    <div className="text-xs text-[#888780] mt-1">
                      Oscillazione attesa ±€{fmt(analytics.total * analytics.wVol)} su €{fmt(analytics.total)} investiti
                    </div>
                    <div className="text-[10px] text-[#888780] mt-2 leading-snug">
                      {analytics.volPartial
                        ? 'Stima parziale — volatilità non disponibile per tutti gli strumenti.'
                        : 'Stima conservativa — la volatilità reale può essere inferiore grazie alla correlazione negativa tra asset.'}
                    </div>
                  </div>
                  {/* Deployment 16 — Rendimento atteso annuo (sostituisce Rischio) */}
                  <div style={{ background: '#252523', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 12, padding: '16px' }} data-testid="portfolio-expected-return">
                    <div className="text-xs text-[#888780] mb-1">Rendimento atteso annuo</div>
                    <div className="text-2xl font-bold text-[#C9A84C]" data-testid="portfolio-expected-return-value">
                      {analytics.expectedReturnPct.toFixed(2)}%/anno
                    </div>
                    <div className="text-xs text-[#888780] mt-1">
                      Crescita attesa €{fmt(analytics.growth10y)} in 10 anni
                    </div>
                    {analytics.retPartial && (
                      <div className="text-[10px] text-[#888780] mt-2 leading-snug">
                        Rendimento atteso parziale — dati non disponibili per tutti gli strumenti.
                      </div>
                    )}
                  </div>
                </div>

                {/* Row 2 — contextual cost comparison (Deployment 15 P1) */}
                <div className="grid md:grid-cols-2 gap-4">
                  {(() => {
                    const BENCHMARK_TER = 0.15;
                    const ter = analytics.wTer;
                    let title, msg, accent = '#C9A84C';
                    if (ter === 0) {
                      title = 'Costi non applicabili';
                      msg = 'Il tuo portafoglio non include ETF con TER — i costi sono le commissioni di trading del tuo broker, non incluse in questo calcolo.';
                      accent = '#888780';
                    } else if (ter < BENCHMARK_TER) {
                      title = 'Portafoglio già ottimizzato';
                      msg = `Stai pagando meno della media di mercato (benchmark: ${BENCHMARK_TER.toFixed(2)}%/anno).`;
                      accent = '#3D7A52';
                    } else if (ter > BENCHMARK_TER) {
                      const saving = Math.max(0, analytics.saving20y);
                      title = 'Risparmio vs ETF low-cost';
                      msg = `Sostituendo con ETF a basso costo risparmieresti €${fmt(saving)} in 20 anni. Benchmark di riferimento: ${BENCHMARK_TER.toFixed(2)}%/anno.`;
                      accent = '#C9A84C';
                    } else {
                      title = 'TER in linea con il mercato';
                      msg = 'Il tuo portafoglio ha un costo in linea con la media di mercato. Considera ETF a TER più basso per ottimizzare.';
                      accent = '#E8903A';
                    }
                    return (
                      <div style={{ background: '#252523', border: `1px solid ${accent}33`, borderRadius: 12, padding: '16px' }} data-testid="portfolio-cost-compare">
                        <div className="text-xs text-[#888780] mb-1">{title}</div>
                        <div className="text-sm leading-relaxed" style={{ color: accent }} data-testid="portfolio-cost-msg">{msg}</div>
                      </div>
                    );
                  })()}
                </div>

                {/* Donut chart */}
                {donutData && (
                  <div style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '20px' }} data-testid="portfolio-donut">
                    <div className="text-sm font-medium text-[#E8E8E4] mb-4">Distribuzione per tipo</div>
                    <div className="max-w-xs mx-auto">
                      <Doughnut data={donutData} options={donutOptions} />
                    </div>
                  </div>
                )}

                {/* P3 — Projection chart with 3 scenarios */}
                <div style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '20px' }} data-testid="portfolio-projection">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                    <div className="text-sm font-medium text-[#E8E8E4] flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-[#C9A84C]" /> Proiezione del portafoglio
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#888780]">Orizzonte:</span>
                      <button onClick={() => setHorizon(h => Math.max(1, h - 1))} aria-label="Diminuisci valore" data-testid="horizon-down"
                        style={{ width: 28, height: 28, borderRadius: 6, background: '#1A1A18', border: '1px solid rgba(255,255,255,0.1)', color: '#E8E8E4', cursor: 'pointer' }}>−</button>
                      <input type="number" min="1" max="30" step="1" value={horizon}
                        onChange={e => setHorizon(Math.max(1, Math.min(30, Number(e.target.value) || 1)))}
                        data-testid="horizon-input"
                        className="w-14 text-center bg-[#1A1A18] border border-white/[0.08] rounded-md py-1 text-[#E8E8E4] text-sm sapiens-no-spinner" />
                      <button onClick={() => setHorizon(h => Math.min(30, h + 1))} aria-label="Aumenta valore" data-testid="horizon-up"
                        style={{ width: 28, height: 28, borderRadius: 6, background: '#1A1A18', border: '1px solid rgba(255,255,255,0.1)', color: '#E8E8E4', cursor: 'pointer' }}>+</button>
                      <span className="text-xs text-[#888780]">anni</span>
                    </div>
                  </div>
                  {projection ? (
                    <>
                      <div style={{ height: 280 }}>
                        <Line data={projection.data} options={projOptions} />
                      </div>
                      <p className="mt-3 text-xs text-[#888780] p-3 rounded-lg" data-testid="projection-mifid-disclaimer"
                        style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}>
                        ⚠️ Le proiezioni mostrate sono stime basate sui rendimenti attesi inseriti. I rendimenti passati non sono indicativi di quelli futuri (MiFID II)
                      </p>
                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        <button onClick={() => setShowRealReturn(v => !v)} data-testid="toggle-real-return"
                          className="text-xs px-3 py-1.5 rounded-lg font-medium"
                          style={{
                            background: showRealReturn ? 'rgba(61,122,82,0.15)' : 'rgba(255,255,255,0.04)',
                            border: `1px solid ${showRealReturn ? '#3D7A52' : 'rgba(255,255,255,0.08)'}`,
                            color: showRealReturn ? '#3D7A52' : '#E8E8E4',
                          }}>
                          {showRealReturn ? '✓ Rendimento reale (− inflazione)' : 'Mostra rendimento reale'}
                        </button>
                        {showRealReturn && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-[#888780]">Inflazione attesa:</span>
                            <input type="number" min="0" max="20" step="0.1" value={inflation}
                              onChange={e => setInflation(e.target.value)}
                              data-testid="inflation-input"
                              className="w-16 text-center bg-[#1A1A18] border border-white/[0.08] rounded-md py-1 text-[#E8E8E4] text-sm sapiens-no-spinner" />
                            <span className="text-xs text-[#888780]">%</span>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="py-12 text-center text-sm text-[#888780]" data-testid="projection-empty">
                      Aggiungi almeno uno strumento per vedere la proiezione del tuo portafoglio nel tempo
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'portfolio' && !analytics && (
              <div className="py-12 text-center text-sm text-[#888780]" data-testid="projection-empty-no-portfolio">
                Aggiungi almeno uno strumento per vedere la proiezione del tuo portafoglio nel tempo
              </div>
            )}

            {activeTab === 'scenarios' && (
              <div className="space-y-4" data-testid="tab-scenarios-content">

                {/* Deployment 16 — Historical chart */}
                <HistoricalAnalysisBox
                  portfolio={portfolio}
                  analytics={analytics}
                  historicalReturns={historicalReturns}
                  startYear={histStartYear}
                  setStartYear={setHistStartYear}
                  amount={histAmount}
                  setAmount={setHistAmount}
                  isOpen={histOpen}
                  onToggle={() => setHistOpen(!histOpen)}
                  currentYear={currentYear}
                />

                {/* Deployment 16 — VAR */}
                <VARRiskBox
                  portfolio={portfolio}
                  analytics={analytics}
                  isOpen={varOpen}
                  onToggle={() => setVarOpen(!varOpen)}
                />

                {/* Existing scenario analysis */}
                <div className="rounded-xl" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)', padding: '16px 20px' }}>
                  <p className="text-sm font-medium text-[#E8E8E4] mb-3">Scenari storici aggregati</p>
                  <p className="text-sm text-[#888780] mb-4">Come avrebbe reagito questo portafoglio nei principali eventi di mercato.</p>
                  <div className="space-y-2">
                  {scenarioResults.map(sc => (
                    <div key={sc.id} style={{ background: '#1C1C1A', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '12px 16px' }} data-testid={`scenario-${sc.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-[#E8E8E4]">{sc.label}</div>
                        <div className="text-right">
                          <div className="text-lg font-bold" style={{ color: sc.diff >= 0 ? '#3D7A52' : '#C0392B' }}>
                            {sc.diff >= 0 ? '+' : ''}€{fmt(Math.abs(sc.diff))}
                          </div>
                          <div className="text-xs" style={{ color: sc.diff >= 0 ? '#3D7A52' : '#C0392B' }}>
                            {sc.pct >= 0 ? '+' : ''}{sc.pct.toFixed(1)}% → €{fmt(sc.finalVal)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  </div>
                  <p className="text-xs text-[#888780] italic mt-3">Scenari ipotetici basati su dati storici. Performance passate non garantiscono risultati futuri.</p>
                </div>
              </div>
            )}

            {activeTab === 'tax' && isPremium && (
              <div className="space-y-3" data-testid="tab-tax-content">
                <p className="text-sm text-[#888780] mb-4">Efficienza fiscale degli strumenti nel tuo portafoglio.</p>
                {portfolio.map(p => {
                  const te = TAX_LABELS[p.inst.tax_efficiency] || TAX_LABELS['standard'];
                  return (
                    <div key={p.inst.id} className="flex items-center justify-between p-4 rounded-xl" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }} data-testid={`tax-${p.inst.id}`}>
                      <div>
                        <div className="font-medium text-[#E8E8E4]">{p.inst.ticker} — {p.inst.name}</div>
                        <div className="text-xs text-[#888780] mt-0.5">{te.note}</div>
                      </div>
                      <div className="text-right">
                        <span className="text-sm" style={{ color: te.color }}>{te.icon} {te.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {portfolio.length === 0 && (
          <div className="text-center py-12 text-[#888780]">
            <div className="text-4xl mb-3">💼</div>
            <div className="text-base">Aggiungi strumenti sopra per iniziare l'analisi</div>
            <div className="text-sm mt-1">Puoi costruire un portafoglio ipotetico per capire costi e diversificazione</div>
          </div>
        )}
      </div>

      {/* Premium prompt modal */}
      {showPremiumPrompt && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="premium-prompt-modal">
          <div style={{ background: '#1C1C1A', border: '1.5px solid rgba(201,168,76,0.3)', borderRadius: 16, padding: '28px', maxWidth: 380, width: '100%' }}>
            <div className="text-lg font-medium text-[#E8E8E4] mb-2">Strumento Premium</div>
            <div className="text-sm text-[#888780] mb-4">
              <strong style={{ color: '#E8E8E4' }}>{showPremiumPrompt.ticker}</strong> è disponibile con SAPIENS Premium.
              <br />€4,99/mese · Prezzo Fondatori fino al 31/08
            </div>
            <div className="flex gap-3">
              <button onClick={() => { navigate('/premium'); setShowPremiumPrompt(null); }}
                className="flex-1 py-2.5 rounded-lg font-semibold text-sm text-[#1C1C1A]" style={{ background: '#C9A84C' }}
                data-testid="premium-prompt-cta">
                Scopri Premium →
              </button>
              <button onClick={() => setShowPremiumPrompt(null)} className="px-4 py-2.5 rounded-lg text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors">
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
